"use client"

import clsx from "clsx"
import React, { useEffect, useState, useRef, useCallback } from "react"

// Throttle utility function
function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null
  let lastExecTime = 0

  return (...args: Parameters<T>) => {
    const currentTime = Date.now()

    if (currentTime - lastExecTime > delay) {
      func(...args)
      lastExecTime = currentTime
    } else {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(
        () => {
          func(...args)
          lastExecTime = Date.now()
        },
        delay - (currentTime - lastExecTime),
      )
    }
  }
}

// Replacement for react-scroll Element component
interface ElementProps {
  name: string
  id?: string
  className?: string
  children: React.ReactNode
}

export const Element: React.FC<ElementProps> = ({
  name,
  id,
  className,
  children,
}) => (
  <div id={id || name} data-scroll-name={name} className={className}>
    {children}
  </div>
)

// Replacement for react-scroll Link component
interface LinkProps {
  to: string
  smooth?: boolean
  offset?: number
  spy?: boolean
  activeClass?: string
  className?: string
  children: React.ReactNode
  onClick?: () => void
}

export const Link: React.FC<LinkProps> = ({
  to,
  smooth = true,
  offset = 0,
  spy = false,
  activeClass,
  className,
  children,
  onClick,
}) => {
  const [isActive, setIsActive] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Throttled scroll handler as fallback for older browsers
  const throttledScrollHandler = useCallback(
    throttle(() => {
      const element =
        document.getElementById(to) ||
        document.querySelector(`[data-scroll-name="${to}"]`)
      if (!element) return

      const rect = element.getBoundingClientRect()
      const isInView = rect.top <= 100 && rect.bottom >= 100
      setIsActive(isInView)
    }, 16), // ~60fps
    [to],
  )

  useEffect(() => {
    if (!spy) return undefined

    const element =
      document.getElementById(to) ||
      document.querySelector(`[data-scroll-name="${to}"]`)

    if (!element) return undefined

    // Use IntersectionObserver if available (modern browsers)
    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      observerRef.current = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Consider element active when it's near the top of the viewport
            const rect = entry.boundingClientRect
            const isInView = rect.top <= 100 && rect.bottom >= 100
            setIsActive(isInView)
          })
        },
        {
          root: null, // Use viewport
          rootMargin: "-100px 0px -100px 0px", // Adjust threshold
          threshold: [0, 0.1, 0.5, 1.0], // Multiple thresholds for smoother detection
        },
      )

      observerRef.current.observe(element)

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect()
        }
      }
    }
    if (typeof window !== "undefined") {
      // Fallback to throttled scroll event for older browsers
      const win = window as Window
      win.addEventListener("scroll", throttledScrollHandler, {
        passive: true,
      })
      throttledScrollHandler() // Check initial state

      return () => {
        win.removeEventListener("scroll", throttledScrollHandler)
      }
    }
    return undefined
  }, [to, spy, throttledScrollHandler])

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()

    const element =
      document.getElementById(to) ||
      document.querySelector(`[data-scroll-name="${to}"]`)

    if (element) {
      const elementPosition = element.offsetTop + offset

      window.scrollTo({
        top: elementPosition,
        behavior: smooth ? "smooth" : "auto",
      })
    }

    if (onClick) {
      onClick()
    }
  }

  const finalClassName = clsx(className, isActive && activeClass)

  return (
    <button onClick={handleClick} className={finalClassName} type="button">
      {children}
    </button>
  )
}
